import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { FaExclamationTriangle } from "react-icons/fa";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { IoClose } from "react-icons/io5";
import Select from "react-select";
import "./ReportConfirmationModal.css";

export interface ReportReasonOption {
  id: number | string;
  reason?: string;
  name?: string;
  title?: string;
}

export interface ReportConfirmationModalProps {
  show: boolean;
  onHide?: () => void;
  onClose?: () => void;
  onSubmit?: (reasonId: any, description: string, attachment?: any) => void | Promise<void>;
  submit?: (values: any) => void | Promise<void>;
  userName?: string;
  reasons?: ReportReasonOption[];
  reportReasonList?: ReportReasonOption[];
  title?: string;
  buttonText?: string;
  isSubmitting?: boolean;
  reportLoader?: boolean;
  disabled?: boolean;
}

const getCustomSelectStyles = (hasError: boolean) => ({
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "12px",
    border: state.isFocused
      ? "1.5px solid #001F68 !important"
      : hasError
      ? "1.5px solid #DC2626 !important"
      : "1.5px solid #CBD5E1 !important",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(0, 31, 104, 0.08)" : "none",
    backgroundColor: "#F8FAFC",
    fontSize: "14px",
    cursor: "pointer",
    paddingLeft: "2px",
    "&:hover": {
      borderColor: state.isFocused ? "#001F68 !important" : "#94A3B8 !important"
    }
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: "12px",
    boxShadow: "0 12px 28px -6px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(226, 232, 240, 0.8)",
    overflow: "hidden",
    zIndex: 9999,
    marginTop: "4px",
    backgroundColor: "#FFFFFF"
  }),
  menuList: (base: any) => ({
    ...base,
    padding: "6px",
    maxHeight: "190px",
    scrollbarWidth: "thin"
  }),
  option: (base: any, state: any) => ({
    ...base,
    borderRadius: "8px",
    padding: "8px 12px",
    fontSize: "13.5px",
    fontWeight: state.isSelected ? 600 : 400,
    cursor: "pointer",
    marginTop: "5px",
    backgroundColor: state.isSelected
      ? "#001F68"
      : state.isFocused
      ? "#F0F4FF"
      : "transparent",
    color: state.isSelected ? "#FFFFFF" : state.isFocused ? "#001F68" : "#0F172A",
    transition: "all 0.15s ease",
    "&:active": {
      backgroundColor: "#001F68",
      color: "#FFFFFF"
    }
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#64748B",
    fontSize: "14px",
    fontWeight: 400
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "#0F172A",
    fontSize: "14px",
    fontWeight: 500
  }),
  input: (base: any) => ({
    ...base,
    color: "#0F172A"
  }),
  indicatorSeparator: () => ({
    display: "none"
  }),
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isFocused ? "#001F68" : "#64748B",
    paddingRight: "10px",
    transition: "transform 0.2s ease",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "none",
    "&:hover": {
      color: "#001F68"
    }
  })
});

const DEFAULT_REPORT_REASONS = [
  { id: "1", name: "Spam or Unwanted Messages" },
  { id: "2", name: "Harassment or Bullying" },
  { id: "3", name: "Inappropriate or Offensive Content" },
  { id: "4", name: "Scam or Fraudulent Activity" },
  { id: "5", name: "Fake Profile or Impersonation" },
  { id: "6", name: "Intellectual Property Infringement" },
  { id: "7", name: "Other Reason" }
];

const ReportConfirmationModal: React.FC<ReportConfirmationModalProps> = ({
  show,
  onHide,
  onClose,
  onSubmit,
  submit,
  userName,
  reasons,
  reportReasonList,
  title,
  buttonText = "Submit Report",
  isSubmitting = false,
  reportLoader = false,
  disabled = false,
}) => {
  const [attachmentFile, setAttachmentFile] = useState("");
  const attachmentRef = useRef<HTMLInputElement | null>(null);

  const loadingState = isSubmitting || reportLoader;

  const handleClose = () => {
    setAttachmentFile("");
    if (attachmentRef.current) {
      attachmentRef.current.value = "";
    }
    if (onHide) onHide();
    else if (onClose) onClose();
  };

  const initialValues = {
    reportReasonId: "",
    description: "",
    attachment: null,
  };

  const validationSchema = Yup.object({
    reportReasonId: Yup.mixed()
      .required("Please select a reason for reporting")
      .nullable(),
  });

  const activeList = (reportReasonList && reportReasonList.length > 0)
    ? reportReasonList
    : (reasons && reasons.length > 0)
    ? reasons
    : DEFAULT_REPORT_REASONS;

  const reasonOptions = useMemo(() => {
    return activeList.map((item: any) => ({
      value: String(item.id),
      label: item.name || item.reason || item.title || `Reason ${item.id}`
    }));
  }, [activeList]);

  useEffect(() => {
    if (!loadingState) {
      setAttachmentFile("");
    }
  }, [loadingState]);

  const handleFormSubmit = async (values: any) => {
    if (disabled || loadingState) return;
    if (submit) {
      await submit(values);
    } else if (onSubmit) {
      await onSubmit(values.reportReasonId, values.description, values.attachment);
    }
  };

  const displayTitle = title || (userName ? `Report ${userName}` : "Report User");

  return (
    <Modal className="ChatConfirmModal ReportChatModal" show={show} onHide={loadingState ? undefined : handleClose} centered backdrop="static">
      <Modal.Body className="p-4 p-sm-4 position-relative">
        {/* Close Button */}
        <button
          type="button"
          className="position-absolute top-0 end-0 m-3 btn btn-sm rounded-circle d-flex align-items-center justify-content-center border-0 text-muted"
          style={{
            width: "32px",
            height: "32px",
            backgroundColor: "#F1F5F9",
            zIndex: 1055,
            transition: "all 0.2s ease",
            cursor: "pointer"
          }}
          onClick={handleClose}
          disabled={loadingState}
          title="Close modal"
        >
          <IoClose size={18} />
        </button>

        {/* Header Icon + Title */}
        <div className="text-center mb-4">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-2 shadow-sm"
            style={{
              width: 56,
              height: 56,
              borderRadius: "18px",
              background: "rgba(220, 38, 38, 0.08)",
              color: "#DC2626",
              fontSize: "24px",
              border: "1px solid rgba(220, 38, 38, 0.15)"
            }}
          >
            <FaExclamationTriangle />
          </div>
          <h3 className="fw-bold mb-1" style={{ fontSize: "20px", color: "#0F172A", letterSpacing: "-0.3px" }}>
            {displayTitle}
          </h3>
          <p className="text-muted small mb-0" style={{ fontSize: "13px" }}>
            Please select a reason to help us keep TechNBuddy safe.
          </p>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
        >
          {({ values, setFieldValue, errors, touched }) => {
            const hasError = !!(errors.reportReasonId && touched.reportReasonId);
            const selectedOption = reasonOptions.find(opt => opt.value === String(values.reportReasonId)) || null;

            return (
              <Form className="text-start">
                {/* Searchable React Select Dropdown */}
                <div className="mb-3">
                  <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>
                    Select Reason <span style={{ color: "#DC2626" }}>*</span>
                  </label>
                  <Select
                    options={reasonOptions}
                    value={selectedOption}
                    onChange={(option: any) => {
                      setFieldValue("reportReasonId", option ? option.value : "");
                    }}
                    placeholder="Search or select a reason..."
                    isSearchable
                    isClearable
                    styles={getCustomSelectStyles(hasError)}
                    noOptionsMessage={() => "No matching reason found"}
                    isDisabled={loadingState}
                  />
                  {hasError && (
                    <p className="mt-1 mb-0" style={{ color: "#DC2626", fontSize: "12px", fontWeight: 500 }}>
                      {errors.reportReasonId as string}
                    </p>
                  )}
                </div>

                {/* Description Field */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155" }}>
                      Report Description <span className="text-muted fw-normal" style={{ fontSize: "12px" }}>(Optional)</span>
                    </label>
                    <span className="text-muted" style={{ fontSize: "11px" }}>
                      {values.description.length}/200
                    </span>
                  </div>
                  <textarea
                    name="description"
                    value={values.description}
                    maxLength={200}
                    onChange={(e) => setFieldValue("description", e.target.value)}
                    placeholder="Provide any additional details or context..."
                    className="form-control shadow-none"
                    rows={3}
                    disabled={loadingState}
                    style={{
                      resize: "none",
                      fontSize: "14px",
                      borderRadius: "12px",
                      border: "1.5px solid #CBD5E1",
                      background: "#F8FAFC",
                      padding: "10px 14px",
                      color: "#0F172A"
                    }}
                  />
                </div>

                {/* Attachment File Field */}
                <div className="mb-4">
                  <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>
                    Attachment <span className="text-muted fw-normal" style={{ fontSize: "12px" }}>(Optional, max 5MB)</span>
                  </label>
                  <input
                    ref={attachmentRef}
                    id="attachment"
                    name="attachment"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,image/webp,application/pdf"
                    className="d-none"
                    disabled={loadingState}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const file = e.target.files?.[0];
                      const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
                      if (!file) return;

                      if (file.size > MAX_FILE_SIZE) {
                        toast.error("File size must be under 5MB");
                        e.target.value = "";
                        return;
                      }

                      const allowedTypes = [
                        "image/jpeg",
                        "image/png",
                        "image/jpg",
                        "image/webp",
                        "application/pdf"
                      ];

                      if (allowedTypes.includes(file.type)) {
                        setFieldValue("attachment", file);
                        setAttachmentFile(file.name);
                      } else {
                        toast.error("Please upload a valid image (JPG, PNG, WEBP) or PDF document.");
                        e.target.value = "";
                      }
                    }}
                  />
                  <div
                    className="d-flex align-items-center justify-content-between px-3 py-2 rounded-3"
                    style={{
                      background: "#F8FAFC",
                      border: "1.5px dashed #CBD5E1",
                      borderRadius: "12px",
                      minHeight: "44px",
                      position: "relative"
                    }}
                  >
                    <label
                      htmlFor="attachment"
                      className="d-flex align-items-center justify-content-between w-100 mb-0 cursor-pointer"
                      style={{ cursor: loadingState ? "not-allowed" : "pointer" }}
                    >
                      {attachmentFile ? (
                        <span className="text-dark small text-truncate fw-medium" style={{ maxWidth: "80%", fontSize: "13px" }}>
                          📄 {attachmentFile}
                        </span>
                      ) : (
                        <span className="text-muted small" style={{ fontSize: "13px" }}>
                          📎 Upload proof/attachment (JPG, PNG, PDF)
                        </span>
                      )}
                    </label>
                    {attachmentFile && (
                      <button
                        type="button"
                        className="btn btn-sm p-0 text-muted border-0 bg-transparent"
                        style={{ position: "absolute", top: "11px", right: "11px" }}
                        onClick={() => {
                          setFieldValue("attachment", null);
                          setAttachmentFile("");
                          if (attachmentRef.current) {
                            attachmentRef.current.value = "";
                          }
                        }}
                        disabled={loadingState}
                        title="Remove attachment"
                      >
                        <IoClose size={18} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center mt-4">
                  <Button
                    type="button"
                    className="w-100 py-2 fw-semibold"
                    style={{
                      borderRadius: "12px",
                      fontSize: "14px",
                      height: "44px",
                      backgroundColor: "#F1F5F9",
                      color: "#475569",
                      border: "1px solid #E2E8F0"
                    }}
                    onClick={handleClose}
                    disabled={loadingState}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    className="w-100 py-2 border-0 fw-semibold shadow-sm d-flex align-items-center justify-content-center gap-2"
                    style={{
                      borderRadius: "12px",
                      fontSize: "14px",
                      height: "44px",
                      backgroundColor: "#001F68",
                      color: "#FFFFFF",
                      transition: "all 0.2s ease"
                    }}
                    disabled={loadingState || disabled}
                  >
                    {loadingState ? (
                      <>
                        <Spinner animation="border" size="sm" variant="light" />
                        <span>Submitting report...</span>
                      </>
                    ) : (
                      buttonText
                    )}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default ReportConfirmationModal;
